import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "@mui/material";
import CustomModal from "../../common/CustomModal";
import StatusChip from "../../common/StatusChip";
import LaunchStatusChip from "../../AdLaunch/LaunchStatusChip";
import ButtonLoader from "../../Icons/ButtonLoader";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { GET_AD_REQUEST_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { formatDisplayDateTime } from "../../../utils/helperFunctions";

// States the Launch table renders with its own chip; the rest are pre-launch pipeline states.
const LAUNCH_CHIP_STATES = new Set(["ready", "scheduled", "queued", "processing", "success", "failed"]);

const HookRow = ({ hook }) => {
  const launch = hook.launch;
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)_auto] items-start gap-3 border-b border-gray-100 py-3 last:border-b-0">
      <span className="pt-0.5 text-sm font-semibold text-gray-800">Hook {hook.hook_number}</span>
      <div className="min-w-0 space-y-1">
        <p className="truncate font-mono text-xs text-gray-500" title={hook.ad_name}>
          {hook.ad_name || "Not delivered yet"}
        </p>
        {launch && (
          <p className="text-xs text-gray-400">
            {launch.meta_ad_id ? <>Meta ad <span className="font-mono text-gray-600">{launch.meta_ad_id}</span></> : "No Meta ad id yet"}
            {launch.launched_at && <> · launched {formatDisplayDateTime(launch.launched_at)}</>}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        {LAUNCH_CHIP_STATES.has(hook.state) ? (
          <LaunchStatusChip status={hook.state} />
        ) : (
          <StatusChip subtle status={hook.state} />
        )}
        {launch?.meta_live_status && (
          <Tooltip
            arrow
            title={`Meta: ${launch.meta_effective_status}${launch.status_synced_at ? ` · checked ${formatDisplayDateTime(launch.status_synced_at)}` : ""}`}
          >
            <span>
              <StatusChip subtle status={launch.meta_live_status} />
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

/** Per-hook launch state of one request, same states and chips as the Ad Launch table. */
const LaunchStatusModal = ({ open, onClose, requestId, adsetName }) => {
  const axiosPrivate = useAxiosPrivate();
  const { data: detail, isLoading } = useQuery({
    queryKey: [...GET_AD_REQUEST_QUERY_KEY, requestId],
    queryFn: () => AdsAPIs.getAdRequest({ axiosPrivate, id: requestId }),
    enabled: open && !!requestId,
  });
  const hooks = detail?.hook_states ?? [];
  return (
    <CustomModal open={open} title="Launch status" onClose={onClose} isButtonsRequired={false} className="lg:!w-[720px]">
      <div className="py-2">
        {adsetName && <p className="mb-3 break-all font-mono text-xs text-gray-500">{adsetName}</p>}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <ButtonLoader color="#7f7ad5" />
            <span className="text-sm text-gray-500">Loading hooks…</span>
          </div>
        ) : hooks.length ? (
          <div className="rounded-lg border border-gray-200 bg-white px-4">
            {hooks.map((h) => (
              <HookRow key={h.hook_number} hook={h} />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">No hooks found for this request.</p>
        )}
      </div>
    </CustomModal>
  );
};

export default LaunchStatusModal;
